/** @format */

/**
 * Trace 分析 — 单 Trace 详情：路由 query 携带 serviceName、traceId、时间范围等。
 */

import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Card,
  dayjs,
  Tabs,
  Tab,
  Split,
  Switch,
  Select,
  Option,
  Breadcrumb,
  BreadcrumbItem
} from '@king-design/react'
import { useLocation, useHistory } from 'react-router'
import { replaceSearchParams } from 'src/utils'
import {
  Descriptions,
  DescriptionItem,
  MonitorSearch,
  CustomPageContainer,
  CopyButton
} from 'src/components'
import { isRouteContainsPath } from 'src/utils/common'
import { useAsync } from 'react-use'
import type { TraceAnalysisDetailResponse, TraceAnalysisDetailSpan } from '../typescript'
import { flattenTraceDetailSpans } from '../constant'
import { secondsFormat } from 'src/utils'
import TraceSpanTree from './TraceSpanTree'
import TraceSpanTopology from './TraceSpanTopology'
import TraceSpanDetail from './TraceSpanDetail'
import { getTraceDetail } from 'src/api/ApiAIObservability'
import { getDefaultSelectableSpan } from './topology'
import { tokenValueFormat } from '../../constant'
import useNavRegion from 'src/hooks/useNavRegion'

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

type ViewType = 'list' | 'topology'

const TraceDetailPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const runtime = query.get('runtime')
  const traceId = query.get('traceId')
  const from = query.get('from')
  const fromSessionId = query.get('sessionId')
  const viewType = query.get('viewType') || 'list'
  const isFromService = isRouteContainsPath('/monitor/aiobservability/servicedetail')
  const agentkitPage = isRouteContainsPath('/monitor/agentkit/runtimedetail')
  const [tab, setTab] = useState<ViewType>(viewType as ViewType)
  // 筛选Span
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [showErrorSpans, setShowErrorSpans] = useState<boolean>(false)
  // 选中的 Span（切换 trace 或 Span 列表变化时校验是否仍存在）
  const [selectedSpan, setSelectedSpan] = useState<TraceAnalysisDetailSpan | null>(null)

  // 控制台region
  const region = useNavRegion()

  const { value: traceDetail } = useAsync(async (): Promise<
    TraceAnalysisDetailResponse | undefined
  > => {
    if (!traceId) {
      return undefined
    }
    const result = await getTraceDetail({ TraceId: traceId })
    if (!result?.TraceId) {
      return undefined
    }
    return {
      ...result,
      Spans: flattenTraceDetailSpans(result.Spans ?? [])
    }
  }, [traceId, region])

  useEffect(() => {
    if (!traceDetail?.Spans?.length || selectedSpan?.SpanId) {
      return
    }
    const defaultSpan = getDefaultSelectableSpan(traceDetail, traceDetail.Spans)
    if (defaultSpan) {
      setSelectedSpan(defaultSpan)
    }
  }, [traceDetail, selectedSpan])

  // 标签
  const tags = useMemo(() => {
    const result = {}
    traceDetail?.Spans.forEach(span => {
      const kind = span.Kind
      if (result[kind]) {
        result[kind]++
      } else {
        result[kind] = 1
      }
    })
    return result
  }, [traceDetail])

  // 异常Span
  const errorSpans = useMemo(() => {
    return traceDetail?.Spans.filter(span => span.StatusCode === 'ERROR')
  }, [traceDetail])

  // 判断标题
  const renderPageTitle = useMemo(() => {
    // 从会话详情跳入
    if (from === 'session') {
      if (isFromService) {
        return (
          <Breadcrumb>
            <BreadcrumbItem to="/monitor/aiobservability/serviceoverview">
              {_$('服务总览')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/aiobservability/servicedetail?serviceName=${traceDetail?.ServiceName}&tab=trace`}
            >
              {_$('服务详情')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/aiobservability/servicedetail/sessionanDetail?serviceName=${traceDetail?.ServiceName}&sessionId=${fromSessionId}&from=service`}
            >
              {_$('会话详情')}
            </BreadcrumbItem>
            <BreadcrumbItem>{traceId ?? '-'}</BreadcrumbItem>
          </Breadcrumb>
        )
      } else {
        return (
          <Breadcrumb>
            <BreadcrumbItem to="/monitor/aiobservability/sessionanalysis">
              {_$('会话分析')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/aiobservability/sessionanalysis/detail?serviceName=${traceDetail?.ServiceName}&sessionId=${fromSessionId}&from=service`}
            >
              {_$('会话详情')}
            </BreadcrumbItem>
            <BreadcrumbItem>{traceId ?? '-'}</BreadcrumbItem>
          </Breadcrumb>
        )
      }
    } else if (agentkitPage) {
      if (from === 'agentkit_session') {
        return (
          <Breadcrumb>
            <BreadcrumbItem to="/monitor/agentkit/overview">
              {_$('AgentKit应用可观测')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/agentkit/runtimedetail?serviceName=${traceDetail?.ServiceName}&runtime=${runtime}&tab=session`}
            >
              {_$('服务详情')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/agentkit/runtimedetail/sessionanDetail?serviceName=${traceDetail?.ServiceName}&runtime=${runtime}&sessionId=${fromSessionId}`}
            >
              {_$('会话详情')}
            </BreadcrumbItem>
            <BreadcrumbItem>{traceId ?? '-'}</BreadcrumbItem>
          </Breadcrumb>
        )
      } else {
        return (
          <Breadcrumb>
            <BreadcrumbItem to="/monitor/agentkit/overview">
              {_$('AgentKit应用可观测')}
            </BreadcrumbItem>
            <BreadcrumbItem
              to={`/monitor/agentkit/runtimedetail?serviceName=${traceDetail?.ServiceName}&runtime=${runtime}&tab=trace`}
            >
              {_$('服务详情')}
            </BreadcrumbItem>
            <BreadcrumbItem>{traceId ?? '-'}</BreadcrumbItem>
          </Breadcrumb>
        )
      }
    } else if (isFromService) {
      return (
        <Breadcrumb>
          <BreadcrumbItem to="/monitor/aiobservability/serviceoverview">
            {_$('服务总览')}
          </BreadcrumbItem>
          <BreadcrumbItem
            to={`/monitor/aiobservability/servicedetail?serviceName=${traceDetail?.ServiceName}&tab=trace`}
          >
            {_$('服务详情')}
          </BreadcrumbItem>
          <BreadcrumbItem>{traceId ?? '-'}</BreadcrumbItem>
        </Breadcrumb>
      )
    } else {
      return (
        <div className="ksc-space">
          <span style={{ marginRight: 4 }}>{traceId || '-'}</span>
          <CopyButton text={traceId} />
        </div>
      )
    }
  }, [from, traceId, traceDetail?.ServiceName, isFromService])

  return (
    <div className={className} id="trace_detail_page">
      <CustomPageContainer
        title={renderPageTitle}
        showBack={from !== 'session' && !isFromService && !agentkitPage}
      >
        <Card type="none" title={_$('基本信息')}>
          <Descriptions columns={3}>
            <DescriptionItem label={_$('服务名称')}>
              <div className="ksc-space">
                <span style={{ marginRight: 4 }}>{traceDetail?.ServiceName ?? '-'}</span>
                <CopyButton text={traceDetail?.ServiceName ?? '-'} />
              </div>
            </DescriptionItem>
            <DescriptionItem label={_$('开始时间')}>
              {traceDetail?.StartTime
                ? dayjs(traceDetail.StartTime).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('耗时')}>
              {secondsFormat(traceDetail?.DurationMs ?? 0)}
            </DescriptionItem>
            <DescriptionItem label={_$('TotalTokens')}>
              {tokenValueFormat(traceDetail?.TotalTokens)}
            </DescriptionItem>
            <DescriptionItem label={_$('输入Token')}>
              {tokenValueFormat(traceDetail?.InputTokens)}
            </DescriptionItem>
            <DescriptionItem label={_$('输出Token')}>
              {tokenValueFormat(traceDetail?.OutputTokens)}
            </DescriptionItem>
          </Descriptions>
        </Card>
        <div className="trace-detail-content">
          <Tabs
            className="trace-detail-tabs"
            type="flat-card"
            value={tab}
            onChangeValue={v => {
              setTab(v as ViewType)
              replaceSearchParams(history, location, { viewType: v })
            }}
          >
            <Tab value="list">{_$('列表')}</Tab>
            <Tab value="topology">{_$('拓扑')}</Tab>
          </Tabs>
          <Card type="none" className="trace-detail-card">
            <div className="trace-details-header">
              <div className="header-left-wrap">
                <div className="header-item-wrap">
                  <p className="header-item-label">
                    {_$('大模型组件标签')}({Object.keys(tags)?.length})
                  </p>
                  <Select multiple clearable value={selectedTags} onChangeValue={setSelectedTags}>
                    {Object.entries(tags).map(([key, value]) => {
                      return (
                        <Option key={key} value={key}>
                          {key}({value})
                        </Option>
                      )
                    })}
                  </Select>
                </div>
                <div className="header-item-wrap">
                  <p className="header-item-label">
                    {_$('查看异常Span')}({errorSpans?.length || 0})
                  </p>
                  <Switch
                    size="small"
                    disabled={errorSpans?.length === 0}
                    value={showErrorSpans}
                    onChangeValue={value => setShowErrorSpans(value)}
                  />
                </div>
              </div>
              <div className="header-search-wrap">
                <MonitorSearch
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder={_$('输入Span关键字')}
                />
              </div>
            </div>
            <Split
              firstSize="70%"
              lastSize="30%"
              max="80%"
              min="20%"
              slotFirst={
                tab === 'list' ? (
                  <TraceSpanTree
                    detail={traceDetail}
                    selectedTags={selectedTags}
                    searchValue={searchValue}
                    showErrorSpans={showErrorSpans}
                    selectedSpanId={selectedSpan?.SpanId ?? null}
                    onSelectSpan={setSelectedSpan}
                  />
                ) : selectedSpan ? (
                  <TraceSpanTopology
                    detail={traceDetail}
                    selectedTags={selectedTags}
                    searchValue={searchValue}
                    showErrorSpans={showErrorSpans}
                    selectedSpanId={selectedSpan?.SpanId ?? null}
                    onSelectSpan={setSelectedSpan}
                  />
                ) : null
              }
              slotLast={
                <TraceSpanDetail
                  span={selectedSpan}
                  sessionId={traceDetail?.SessionId}
                  traceId={traceId}
                />
              }
            ></Split>
          </Card>
        </div>
      </CustomPageContainer>
    </div>
  )
}

export default styled(TraceDetailPage)`
  .trace-detail-tabs {
    margin-top: 16px;
    padding: 0;
    border-radius: 4px 4px 0 0 !important;
    .k-tabs-active-bar {
      height: 100%;
      border-radius: 4px 4px 0 0;
    }
  }
  .trace-detail-card {
    border-radius: 0 0 4px 4px;
    box-shadow: none;
    .trace-details-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 16px;
      .header-left-wrap {
        display: flex;
        align-items: center;
        .header-item-wrap {
          display: flex;
          align-items: center;
          margin-right: 16px;
          .header-item-label {
            margin-right: 8px;
            margin-bottom: 0;
          }
        }
      }
    }
  }
  .k-split-line {
    border-left: none !important;
    border-right: none !important;
  }
`
